// GraphQL queries that exactly match your Python DEX implementations

// ============================================================================
// UNISWAP V3 QUERIES
// ============================================================================

export const UNISWAP_V3_POOL_COMBINED_QUERY = `
  query($pool: String!, $first: Int!, $skip: Int!, $dateGte: Int!) {
    # Get pool metadata + live state (used to synthesize a fresh "today" snapshot)
    liquidityPool(id: $pool) {
      id
      inputTokens {
        id
        symbol
        name
        decimals
      }
      totalValueLockedUSD
      inputTokenBalances
      inputTokenBalancesUSD
      cumulativeVolumeUSD
      cumulativeSupplySideRevenueUSD
      cumulativeProtocolSideRevenueUSD
      cumulativeTotalRevenueUSD
    }
    # Get historical snapshots
    liquidityPoolDailySnapshots(
      first: $first
      skip: $skip
      orderBy: timestamp
      orderDirection: desc
      where: { pool: $pool, timestamp_gte: $dateGte }
    ) {
      timestamp
      totalValueLockedUSD
      dailyVolumeUSD
      cumulativeVolumeUSD
      inputTokenBalances
      inputTokenBalancesUSD
      dailySupplySideRevenueUSD
      dailyProtocolSideRevenueUSD
      dailyTotalRevenueUSD
    }
  }
`;

export const UNISWAP_V3_POOL_METADATA_QUERY = `
  query($pool: String!) {
    liquidityPool(id: $pool) {
      id
      inputTokens { 
        id 
        symbol 
        name 
        decimals 
      }
    }
  }
`;

// ============================================================================
// PANCAKESWAP V3 QUERIES
// ============================================================================

export const PANCAKESWAP_V3_POOL_COMBINED_QUERY = `
  query($pool: String!, $first: Int!, $skip: Int!, $dateGte: Int!) {
    # Get pool metadata + live state
    pool(id: $pool) {
      id
      token0 { id symbol name decimals }
      token1 { id symbol name decimals }
      totalValueLockedUSD
      totalValueLockedToken0
      totalValueLockedToken1
      volumeUSD
      feesUSD
      token0Price
      token1Price
    }
    # Get historical snapshots
    poolDayDatas(
      first: $first
      skip: $skip
      orderBy: date
      orderDirection: desc
      where: { pool: $pool, date_gte: $dateGte }
    ) {
      date
      tvlUSD
      volumeUSD
      feesUSD
      protocolFeesUSD
    }
  }
`;

export const PANCAKESWAP_V3_POOL_METADATA_QUERY = `
  query($pool: String!) {
    pool(id: $pool) {
      id
      token0 { id symbol name decimals }
      token1 { id symbol name decimals }
    }
  }
`;

// ============================================================================
// SUSHISWAP V3 QUERIES
// ============================================================================

export const SUSHISWAP_V3_POOL_COMBINED_QUERY = `
  query($pool: String!, $first: Int!, $dateGte: Int!) {
    # Get pool metadata + live state
    pool(id: $pool) {
      id
      token0 { symbol decimals }
      token1 { symbol decimals }
      feeTier
      totalValueLockedUSD
      totalValueLockedToken0
      totalValueLockedToken1
      volumeUSD
      feesUSD
      token0Price
      token1Price
      liquidity
    }
    # Get historical snapshots
    poolDayDatas(
      first: $first
      orderBy: date
      orderDirection: asc
      where: { pool: $pool, date_gte: $dateGte }
    ) {
      date
      tvlUSD
      volumeUSD
      feesUSD
      token0Price
      token1Price
      liquidity
      txCount
    }
  }
`;

export const SUSHISWAP_V3_POOL_METADATA_QUERY = `
  query($pool: String!) {
    pool(id: $pool) {
      id
      token0 { symbol decimals }
      token1 { symbol decimals }
      feeTier
    }
  }
`;

// ============================================================================
// UNISWAP V4 QUERIES
// ============================================================================

export const UNISWAP_V4_POOL_COMBINED_QUERY = `
  query($pool: String!, $first: Int!, $skip: Int!, $dateGte: Int!) {
    # Get pool metadata + live state
    pool(id: $pool) {
      id
      feeTier
      token0 { id symbol name decimals }
      token1 { id symbol name decimals }
      totalValueLockedUSD
      totalValueLockedToken0
      totalValueLockedToken1
      volumeUSD
      volumeToken0
      volumeToken1
      feesUSD
    }
    # Get historical snapshots
    poolDayDatas(
      first: $first
      skip: $skip
      orderBy: date
      orderDirection: desc
      where: { pool: $pool, date_gte: $dateGte }
    ) {
      date
      tvlUSD
      volumeUSD
      volumeToken0
      volumeToken1
    }
  }
`;

export const UNISWAP_V4_POOL_METADATA_QUERY = `
  query($pool: String!) {
    pool(id: $pool) {
      id
      feeTier
      token0 { id symbol name decimals }
      token1 { id symbol name decimals }
    }
  }
`;

// ============================================================================
// UNISWAP V4 TOKEN PRICES QUERY (for volume calculation)
// ============================================================================

export const UNISWAP_V4_TOKEN_PRICES_QUERY = `
  query($token0: String!, $token1: String!, $from: Int!, $to: Int!) {
    t0: tokenDayDatas(
      where: { token: $token0, date_gte: $from, date_lte: $to }
      orderBy: date
      orderDirection: asc
      first: 1000
    ) {
      date
      priceUSD
    }
    t1: tokenDayDatas(
      where: { token: $token1, date_gte: $from, date_lte: $to }
      orderBy: date
      orderDirection: asc
      first: 1000
    ) {
      date
      priceUSD
    }
  }
`;

// ============================================================================
// TEST QUERIES
// ============================================================================

export const TEST_CONNECTION_QUERY = `
  query {
    _meta {
      hasIndexingErrors
      block {
        number
        hash
      }
    }
  }
`;

// ============================================================================
// QUERY VARIABLES TYPES
// ============================================================================

export interface PoolSnapshotsVariables {
  pool: string;
  first: number;
  skip: number;
  dateGte: number;
}

export interface PoolMetadataVariables {
  pool: string;
}

export interface TokenPricesVariables {
  token0: string;
  token1: string;
  from: number;
  to: number;
}
